/**
 * Invoice Export Formatter Utilities
 * Formats data for various export formats (Excel, CSV, PDF, JSON)
 */

class InvoiceExportFormatter {
  /**
   * Format data for Excel export
   */
  formatForExcel(data, columns) {
    const formattedData = []

    // Add header row
    const headerRow = columns.map(col => col.label || col.key)
    formattedData.push(headerRow)

    // Add data rows
    data.forEach(row => {
      const formattedRow = columns.map(col => {
        const value = this.getNestedValue(row, col.key)
        return this.formatCellValue(value, col.type)
      })
      formattedData.push(formattedRow)
    })

    return formattedData
  }

  /**
   * Format data for CSV export
   */
  formatForCSV(data, columns) {
    const rows = []

    // Add header row
    const headerRow = columns.map(col => `"${col.label || col.key}"`).join(',')
    rows.push(headerRow)

    // Add data rows
    data.forEach(row => {
      const formattedRow = columns.map(col => {
        const value = this.getNestedValue(row, col.key)
        const formattedValue = this.formatCellValue(value, col.type)
        return `"${String(formattedValue).replace(/"/g, '""')}"`
      }).join(',')
      rows.push(formattedRow)
    })

    return rows.join('\n')
  }

  /**
   * Format data for JSON export
   */
  formatForJSON(data, columns) {
    return data.map(row => {
      const formattedRow = {}
      columns.forEach(col => {
        const value = this.getNestedValue(row, col.key)
        formattedRow[col.key] = this.formatCellValue(value, col.type)
      })
      return formattedRow
    })
  }

  /**
   * Format reconciliation data
   */
  formatReconciliationData(data) {
    const columns = [
      { key: 'invoice_id', label: 'Invoice ID', type: 'string' },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
      { key: 'client', label: 'Client', type: 'string' },
      { key: 'base_amount', label: 'Base Amount', type: 'currency' },
      { key: 'gst_amount', label: 'GST Amount', type: 'currency' },
      { key: 'total_amount', label: 'Total Amount', type: 'currency' },
      { key: 'paid_amount', label: 'Paid Amount', type: 'currency' },
      { key: 'pending_amount', label: 'Pending Amount', type: 'currency' },
      { key: 'due_date', label: 'Due Date', type: 'date' },
      { key: 'status', label: 'Status', type: 'string' },
      { key: 'booking_status', label: 'Booking Status', type: 'string' }
    ]

    return {
      columns,
      formattedData: this.formatForExcel(data, columns)
    }
  }

  /**
   * Format compliance data
   */
  formatComplianceData(data) {
    const columns = [
      { key: 'invoice_id', label: 'Invoice ID', type: 'string' },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
      { key: 'client', label: 'Client', type: 'string' },
      { key: 'client_gst', label: 'Client GST', type: 'string' },
      { key: 'sattva_gst', label: 'Sattva GST', type: 'string' },
      { key: 'gst_type', label: 'GST Type', type: 'string' },
      { key: 'einvoice_filed', label: 'E-Invoice Filed', type: 'boolean' },
      { key: 'einvoice_date', label: 'E-Invoice Date', type: 'date' },
      { key: 'tds_applicable', label: 'TDS Applicable', type: 'boolean' },
      { key: 'tds_percentage', label: 'TDS Percentage', type: 'number' },
      { key: 'tds_amount', label: 'TDS Amount', type: 'currency' }
    ]

    return {
      columns,
      formattedData: this.formatForExcel(data, columns)
    }
  }

  /**
   * Format financial data
   */
  formatFinancialData(data) {
    const columns = [
      { key: 'invoice_id', label: 'Invoice ID', type: 'string' },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
      { key: 'billing_month', label: 'Billing Month', type: 'string' },
      { key: 'client', label: 'Client', type: 'string' },
      { key: 'category', label: 'Category', type: 'string' },
      { key: 'base_amount', label: 'Base Amount', type: 'currency' },
      { key: 'gst_amount', label: 'GST Amount', type: 'currency' },
      { key: 'tds_amount', label: 'TDS Amount', type: 'currency' },
      { key: 'total_amount', label: 'Total Amount', type: 'currency' },
      { key: 'paid_amount', label: 'Paid Amount', type: 'currency' },
      { key: 'pending_amount', label: 'Pending Amount', type: 'currency' },
      { key: 'payment_date', label: 'Payment Date', type: 'date' }
    ]

    return {
      columns,
      formattedData: this.formatForExcel(data, columns)
    }
  }

  /**
   * Format tax data
   */
  formatTaxData(data) {
    const columns = [
      { key: 'invoice_id', label: 'Invoice ID', type: 'string' },
      { key: 'invoice_date', label: 'Invoice Date', type: 'date' },
      { key: 'client', label: 'Client', type: 'string' },
      { key: 'gst_type', label: 'GST Type', type: 'string' },
      { key: 'cgst_amount', label: 'CGST Amount', type: 'currency' },
      { key: 'sgst_amount', label: 'SGST Amount', type: 'currency' },
      { key: 'igst_amount', label: 'IGST Amount', type: 'currency' },
      { key: 'total_gst', label: 'Total GST', type: 'currency' },
      { key: 'tds_amount', label: 'TDS Amount', type: 'currency' },
      { key: 'net_amount', label: 'Net Amount', type: 'currency' }
    ]

    return {
      columns,
      formattedData: this.formatForExcel(data, columns)
    }
  }

  /**
   * Format audit trail data
   */
  formatAuditTrailData(data) {
    const columns = [
      { key: 'timestamp', label: 'Timestamp', type: 'datetime' },
      { key: 'action', label: 'Action', type: 'string' },
      { key: 'user', label: 'User', type: 'string' },
      { key: 'field', label: 'Field', type: 'string' },
      { key: 'old_value', label: 'Old Value', type: 'string' },
      { key: 'new_value', label: 'New Value', type: 'string' },
      { key: 'remarks', label: 'Remarks', type: 'string' }
    ]

    return {
      columns,
      formattedData: this.formatForExcel(data, columns)
    }
  }

  /**
   * Get nested value from object using dot notation
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, prop) => {
      return current && current[prop] !== undefined ? current[prop] : null
    }, obj)
  }

  /**
   * Format cell value based on type
   */
  formatCellValue(value, type) {
    if (value === null || value === undefined) {
      return ''
    }

    switch (type) {
      case 'currency':
        return typeof value === 'number' ? value.toFixed(2) : parseFloat(value || 0).toFixed(2)
      case 'date':
        if (!value) return ''
        const date = new Date(value)
        return date.toISOString().split('T')[0]
      case 'datetime':
        if (!value) return ''
        return new Date(value).toISOString()
      case 'boolean':
        return value ? 'Yes' : 'No'
      case 'number':
        return typeof value === 'number' ? value : parseFloat(value || 0)
      case 'percentage':
        return typeof value === 'number' ? `${value}%` : `${value}%`
      default:
        return String(value)
    }
  }

  /**
   * Generate filename for export
   */
  generateFilename(prefix, filters = {}) {
    const date = new Date().toISOString().split('T')[0]
    let filename = `${prefix}_${date}`

    if (filters.startDate && filters.endDate) {
      filename += `_${filters.startDate}_to_${filters.endDate}`
    }

    return filename
  }
}

export default new InvoiceExportFormatter()
